import React from "react";
import { useTranslation } from "react-i18next";
import ListToolbar from "../../components/ui/ListToolbar";
import AppTable from "../../components/ui/AppTable";
import AppButton from "../../components/ui/AppButton";
import { formatListDate } from "../../utils/formatters";

const ACTION_META = {
  CREATE:  { color: "success", icon: "fa-plus-circle" },
  UPDATE:  { color: "primary", icon: "fa-edit" },
  DELETE:  { color: "danger",  icon: "fa-trash" },
  APPROVE: { color: "success", icon: "fa-check-circle" },
  REJECT:  { color: "warning", icon: "fa-times-circle" },
};

const ROLE_META = {
  SUPER_ADMIN:        { color: "danger",  icon: "fa-crown" },
  UNIVERSITY_MANAGER: { color: "primary", icon: "fa-user-tie" },
  ADVISOR:            { color: "info",    icon: "fa-chalkboard-teacher" },
  CLUB_ADMIN:         { color: "warning", icon: "fa-users-cog" },
};

const TARGET_ICON = {
  UNIVERSITY: "fa-university",
  CAMPUS:     "fa-building",
  FACULTY:    "fa-graduation-cap",
  DEPARTMENT: "fa-layer-group",
  CLUB:       "fa-users",
  EVENT:      "fa-calendar-alt",
  USER:       "fa-user",
  AUTHORITY:  "fa-user-shield",
};

const UMLogRecordsList = ({ logs, loading, totalElements, filters, onFilterChange, onClearFilters, i18nLanguage }) => {
  const { t } = useTranslation("universityManager");

  const getActionBadge = (actionType) => {
    if (!actionType) return null;
    const prefix = actionType.split("_")[0];
    const meta = ACTION_META[prefix] || { color: "secondary", icon: "fa-circle" };
    const label = t(`logs.actionTypes.${actionType}`, { defaultValue: actionType });
    return (
      <span className={`badge bg-${meta.color} bg-opacity-10 text-${meta.color} fw-semibold`} style={{ fontSize: "0.75rem" }}>
        <i className={`fas ${meta.icon} me-1`}></i>
        {label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    if (!role) return null;
    const meta = ROLE_META[role] || { color: "secondary", icon: "fa-user" };
    const label = t(`logs.roles.${role}`, { defaultValue: role });
    return (
      <span className={`badge bg-${meta.color} bg-opacity-10 text-${meta.color}`} style={{ fontSize: "0.7rem" }}>
        <i className={`fas ${meta.icon} me-1`}></i>
        {label}
      </span>
    );
  };

  const isLocaleLang = i18nLanguage?.toLowerCase().startsWith("tr");

  const getTargetCell = (targetName, targetNameLocal, targetType) => {
    const iconClass = TARGET_ICON[targetType] || "fa-circle";
    const label = targetType ? t(`logs.targetTypes.${targetType}`, { defaultValue: targetType }) : "";
    const displayName = (isLocaleLang && targetNameLocal) ? targetNameLocal : (targetName || "—");
    return (
      <div className="d-flex align-items-center gap-2">
        <div
          className="rounded-circle bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: "34px", height: "34px" }}
        >
          <i className={`fas ${iconClass} text-secondary`} style={{ fontSize: "0.8rem" }}></i>
        </div>
        <div>
          <div className="fw-semibold text-dark" style={{ fontSize: "0.875rem" }}>{displayName}</div>
          <small className="text-muted" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</small>
        </div>
      </div>
    );
  };

  const hasFilters = Object.values(filters).some((value) => value);

  return (
    <div className="um-log-records-list">
      <ListToolbar
        title={t("logs.list.title")}
        subtitle={t("logs.list.subtitle", { count: totalElements })}
      >
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label fw-medium">{t("logs.filters.dateFrom")}</label>
            <input type="date" className="form-control" value={filters.dateFrom} onChange={(e) => onFilterChange("dateFrom", e.target.value)} />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-medium">{t("logs.filters.dateTo")}</label>
            <input type="date" className="form-control" value={filters.dateTo} onChange={(e) => onFilterChange("dateTo", e.target.value)} />
          </div>
          <div className="col-md-2">
            <label className="form-label fw-medium">{t("logs.filters.actionType")}</label>
            <select className="form-select" value={filters.actionType} onChange={(e) => onFilterChange("actionType", e.target.value)}>
              <option value="">{t("logs.filters.all")}</option>
              {["UPDATE_UNIVERSITY","CREATE_CAMPUS","UPDATE_CAMPUS","DELETE_CAMPUS","CREATE_FACULTY","UPDATE_FACULTY","DELETE_FACULTY","CREATE_DEPARTMENT","UPDATE_DEPARTMENT","DELETE_DEPARTMENT","CREATE_CLUB","UPDATE_CLUB","DELETE_CLUB","APPROVE_EVENT","REJECT_EVENT","DELETE_EVENT","CREATE_AUTHORITY","UPDATE_AUTHORITY","DELETE_AUTHORITY","CREATE_USER","UPDATE_USER","UPDATE_USER_STATUS","DELETE_USER"].map((key) => (
                <option key={key} value={key}>{t(`logs.actionTypes.${key}`)}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label fw-medium">{t("logs.filters.actorRole")}</label>
            <select className="form-select" value={filters.actorRole} onChange={(e) => onFilterChange("actorRole", e.target.value)}>
              <option value="">{t("logs.filters.all")}</option>
              {["SUPER_ADMIN","UNIVERSITY_MANAGER","ADVISOR","CLUB_ADMIN"].map((key) => (
                <option key={key} value={key}>{t(`logs.roles.${key}`)}</option>
              ))}
            </select>
          </div>
          <div className="col-md-1">
            <label className="form-label fw-medium">{t("logs.filters.targetType")}</label>
            <select className="form-select" value={filters.targetType} onChange={(e) => onFilterChange("targetType", e.target.value)}>
              <option value="">{t("logs.filters.all")}</option>
              {["UNIVERSITY","CAMPUS","FACULTY","DEPARTMENT","CLUB","EVENT","USER","AUTHORITY"].map((key) => (
                <option key={key} value={key}>{t(`logs.targetTypes.${key}`)}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label fw-medium">{t("logs.filters.search")}</label>
            <input type="text" className="form-control" value={filters.search} onChange={(e) => onFilterChange("search", e.target.value)} placeholder={t("logs.filters.searchPlaceholder")} />
          </div>
          {hasFilters && (
            <div className="col-auto d-flex align-items-end">
              <AppButton variant="outline" className="btn-sm" onClick={onClearFilters}>
                <i className="fas fa-times me-1"></i>
                {t("logs.filters.clear")}
              </AppButton>
            </div>
          )}
        </div>
      </ListToolbar>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : (
            <AppTable responsive>
              <thead>
                <tr>
                  <th className="ps-4">{t("logs.columns.dateTime")}</th>
                  <th>{t("logs.columns.who")}</th>
                  <th>{t("logs.columns.action")}</th>
                  <th>{t("logs.columns.target")}</th>
                  <th>{t("logs.columns.details")}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((item) => (
                  <tr key={item.id} className="border-bottom">
                    <td className="ps-4">
                      <div className="fw-semibold text-dark" style={{ fontSize: "0.875rem" }}>
                        {formatListDate(item.timestamp, i18nLanguage)}
                      </div>
                      <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                        {item.timestamp ? new Date(item.timestamp).toLocaleTimeString(i18nLanguage, { hour: "2-digit", minute: "2-digit" }) : ""}
                      </small>
                    </td>
                    <td>
                      <div className="fw-bold text-dark" style={{ fontSize: "0.875rem" }}>{item.actorName}</div>
                      <div className="mt-1">{getRoleBadge(item.actorRole)}</div>
                    </td>
                    <td>{getActionBadge(item.actionType)}</td>
                    <td>{getTargetCell(item.targetName, item.targetNameLocal, item.targetType)}</td>
                    <td>
                      <span
                        className="text-muted"
                        style={{ fontSize: "0.85rem", display: "block", maxWidth: "260px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={i18nLanguage?.toLowerCase().startsWith("tr") ? item.messageTr : item.messageEn}
                      >
                        {i18nLanguage?.toLowerCase().startsWith("tr") ? item.messageTr : item.messageEn}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </AppTable>
          )}
        </div>
      </div>
    </div>
  );
};

export default UMLogRecordsList;
